import type { DelegateArchive, DelegateTimelineEntry } from "./types";

export type TimelineScope = { companyId: string; runId: string; requestMessageId: string; provider?: "astra" | "claude" };
const ID = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$/;
export function validArchive(value: DelegateArchive | undefined) {
  return value?.schemaVersion === 1 && typeof value.runId === "string" && ID.test(value.runId) &&
    typeof value.requestMessageId === "string" && ID.test(value.requestMessageId) &&
    Number.isSafeInteger(value.lastSequence) && value.lastSequence >= 0 ? value : undefined;
}
export function timelinePath(scope: TimelineScope) {
  return "/api/sessions/" + encodeURIComponent(scope.companyId) + (scope.provider === "claude" ? "/claude-runs/" : "/delegate-runs/") +
    encodeURIComponent(scope.runId) + "/updates";
}
export function safeTimelineImage(url: string | undefined, scope: TimelineScope) {
  if (!url) return null;
  const prefix = timelinePath(scope) + "/media/";
  if (!url.startsWith(prefix)) return null;
  const [filename, query = ""] = url.slice(prefix.length).split("?");
  if (!/^[a-f0-9]{64}$/.test(filename!) || url.includes("#")) return null;
  const params = new URLSearchParams(query);
  if ([...params.keys()].length !== 2 || params.get("requestMessageId") !== scope.requestMessageId ||
      !/^[1-9]\d{0,15}$/.test(params.get("sequence") ?? "")) return null;
  return url;
}
export function parseTimelinePage(value: unknown, scope: TimelineScope) {
  const page = value as Record<string, unknown> | null;
  if (!page || page.schemaVersion !== 1 || page.runId !== scope.runId ||
      page.requestMessageId !== scope.requestMessageId || !Array.isArray(page.entries) ||
      page.entries.length > 50) throw new Error("Invalid update archive");
  const entries: DelegateTimelineEntry[] = [];
  for (const raw of page.entries) {
    const entry = raw as DelegateTimelineEntry;
    if (!entry || !Number.isSafeInteger(entry.sequence) || Number(entry.sequence) < 1 ||
        typeof entry.id !== "string" || entry.id.length > 200 ||
        !Number.isFinite(Date.parse(entry.updatedAt))) continue;
    if (entry.type === "commentary" && typeof entry.text === "string" && entry.text.trim() &&
        entry.text.length <= 3000) entries.push({id:entry.id, sequence:entry.sequence, type:"commentary",
          updatedAt:entry.updatedAt, text:entry.text, attempt:entry.attempt,
          ...(scope.provider==="claude" && /^cpu_[0-9a-f-]{36}$/.test(entry.segmentId??"") ? {segmentId:entry.segmentId} : {})});
    if (entry.type === "image" && entry.imageStatus === "expired" && !entry.image) {
      entries.push({id:entry.id,sequence:entry.sequence,type:"image",updatedAt:entry.updatedAt,
        attempt:entry.attempt,imageStatus:"expired",text:"Images cleared after 7 days"});
    } else if (entry.type === "image" && safeTimelineImage(entry.image?.url, scope) &&
        new URLSearchParams(entry.image!.url.split("?")[1]).get("sequence") === String(entry.sequence)) {
      entries.push({id:entry.id,sequence:entry.sequence,type:"image",updatedAt:entry.updatedAt,
        attempt:entry.attempt,
        ...(scope.provider==="claude" && /^cpu_[0-9a-f-]{36}$/.test(entry.segmentId??"") ? {segmentId:entry.segmentId} : {}),
        image:{url:entry.image!.url,alt:typeof entry.image?.alt === "string"
          ? entry.image.alt.slice(0,240) : "Browser screenshot from " + (scope.provider === "claude" ? "Claude" : "Astra")}});
    }
  }
  const cursor = (value: unknown) => typeof value === "string" && /^\d{1,16}$/.test(value) ? value : null;
  return {entries, olderCursor:cursor(page.olderCursor), afterCursor:cursor(page.afterCursor),
    hasMoreAfter:page.hasMoreAfter === true};
}
/** Loaded entries never fall out of the UI when the compact status window advances. */
export function mergeTimelineEntries(previous: DelegateTimelineEntry[], incoming: DelegateTimelineEntry[]) {
  const values = new Map<string, DelegateTimelineEntry>();
  for (const entry of [...previous, ...incoming]) {
    const key = entry.sequence ? "s:" + entry.sequence : "f:" + entry.updatedAt + ":" + entry.text;
    values.set(key, entry);
  }
  const entries = [...values.values()];
  const archivedComments = new Set(entries.filter(e => e.sequence && e.type === "commentary")
    .map(e => e.updatedAt));
  // Archive and compact snapshot share their event timestamp. The archived
  // text may be longer or retain a safe link omitted from the legacy snapshot.
  return entries.filter(e => e.sequence || !archivedComments.has(e.updatedAt))
    .sort((a,b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt) || (a.sequence ?? 0) - (b.sequence ?? 0));
}
